import React, { useMemo, useState } from 'react';
import { useAction } from 'wasp/client/operations';
import { createFoodCourtItem } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { 
  ArrowLeft,
  Utensils,
  Tag, 
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Coffee,
  Star,
  Clock
} from 'lucide-react';
import { MenuUploadField } from './MenuUploadField';
import type { FoodCourtMenuMetadata } from './menuTypes';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';

interface FoodCourtFormData {
  title: string;
  description: string;
  menu: FoodCourtMenuMetadata | null;
  category: 'FOOD' | 'BEVERAGE' | 'DESSERT' | 'SNACK';
  cuisine: string;
  dietaryInfo: string[];
  availability: 'AVAILABLE' | 'LIMITED' | 'OUT_OF_STOCK';
  contactInfo: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const FOOD_CATEGORIES = [
  { value: 'FOOD', label: 'Food', icon: Utensils },
  { value: 'BEVERAGE', label: 'Beverage', icon: Coffee },
  { value: 'DESSERT', label: 'Dessert', icon: Star },
  { value: 'SNACK', label: 'Snack', icon: Tag }
];

const AVAILABILITY_OPTIONS = [
  { value: 'AVAILABLE', label: 'Available', description: 'Currently available for order' },
  { value: 'LIMITED', label: 'Limited', description: 'Limited quantity available' },
  { value: 'OUT_OF_STOCK', label: 'Out of Stock', description: 'Currently unavailable' }
];

const DIETARY_OPTIONS = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut-Free', 
  'Local', 'Kosher', 'Low-Carb', 'Keto', 'Organic'
];


// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function AddFoodCourtPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const params = useParams<{ slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const menuUploadKey = useMemo(() => crypto.randomUUID(), []);
  
  const createFoodCourtItemFn = useAction(createFoodCourtItem);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<FoodCourtFormData>({
    title: '',
    description: '',
    menu: null,
    category: 'FOOD',
    cuisine: '',
    dietaryInfo: [],
    availability: 'AVAILABLE',
    contactInfo: '',
    visibility: 'VERIFIED'
  });

  const handleChange = (field: keyof FoodCourtFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDietaryChange = (diet: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      dietaryInfo: checked 
        ? [...prev.dietaryInfo, diet]
        : prev.dietaryInfo.filter(d => d !== diet)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await createFoodCourtItemFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        menu: formData.menu,
        category: formData.category,
        cuisine: formData.cuisine.trim(),
        dietaryInfo: formData.dietaryInfo,
        availability: formData.availability,
        contactInfo: formData.contactInfo.trim(),
        visibility: formData.visibility,
        submissionType: 'STANDARD'
      });

      // Navigate back to the food court page
      handleGoBack();
    } catch (error) {
      console.error('Error creating food court item:', error);
      setErrors({ submit: 'Failed to create item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(locationSectionPath(locSlug, 'food-court'));
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to post food court items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex h-full">
        {/* Main Content Area - Takes full width excluding left sidebar */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 p-6 pb-32 lg:pb-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Utensils className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Add Food Court Item</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="food-court-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* What are you posting? */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What are you offering?</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="e.g., Chicken Teriyaki Bowl, Fresh Smoothie, Chocolate Cake"
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="Describe your food item, ingredients, preparation method, special features..."
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Category and Cuisine */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleChange('category', e.target.value as 'FOOD' | 'BEVERAGE' | 'DESSERT' | 'SNACK')}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {FOOD_CATEGORIES.map(category => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Cuisine Type</label>
                        <input
                          type="text"
                          value={formData.cuisine}
                          onChange={(e) => handleChange('cuisine', e.target.value)}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="e.g., Italian, Asian, Mexican"
                        />
                      </div>
                    </div>

                    {/* Menu (admin) and Availability */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {isAdministrator(user) && (
                        <div>
                          <MenuUploadField
                            locationSlug={locSlug ?? 'location'}
                            itemKey={menuUploadKey}
                            value={formData.menu}
                            onChange={(menu) => handleChange('menu', menu)}
                            disabled={isSubmitting}
                          />
                        </div>
                      )}

                      <div className={isAdministrator(user) ? '' : 'sm:col-span-2'}>
                        <label className="block text-sm font-medium text-foreground mb-2">Availability</label>
                        <select
                          value={formData.availability}
                          onChange={(e) => handleChange('availability', e.target.value as 'AVAILABLE' | 'LIMITED' | 'OUT_OF_STOCK')}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {AVAILABILITY_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Dietary Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Dietary Information</span>
                          <Tooltip content="Select all that apply to help customers with dietary restrictions">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {DIETARY_OPTIONS.map(diet => (
                          <label key={diet} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.dietaryInfo.includes(diet)}
                              onChange={(e) => handleDietaryChange(diet, e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{diet}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Contact Information</span>
                          <Tooltip content="How customers can reach you for orders">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.contactInfo}
                          onChange={(e) => handleChange('contactInfo', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Phone number or email"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="hidden lg:flex w-80 border-l border-border flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="food-court-form"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Posting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Tips for Better Results */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tips for Better Results</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Use appetizing titles that describe the dish clearly</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Include key ingredients and preparation details</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Upload a PDF or image so visitors can view your menu from the list</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Provide accurate dietary information for customer safety</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Keep availability status updated to avoid confusion</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleGoBack}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="food-court-form"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : 'Post'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}



